import { AppointmentFileReferenceStatus, FileType } from "~/api/API";
import { FileDisplayData } from "~/api/queries/useAppointmentFiles";
import { LinkedAppointmentInfo } from "~/api/queries/useLinkedAppointmentFiles";

/**
 * File types that are NOT inheritable across reschedules.
 * These are "current" only on the appointment that originally uploaded them.
 *
 * Mirrors RESCHEDULE_INHERITED_READ_ONLY_FILE_TYPES in services/uploadedFile.ts.
 */
const NON_INHERITABLE_FILE_TYPES: FileType[] = [
  FileType.QME_NOTIFICATION_FORM,
  FileType.APPOINTMENT_NOTICE,
  FileType.LATE_CANCEL_NOTICE,
  FileType.LATE_RESCHEDULE_NOTICE,
];

interface ResolveFileOwnershipParams {
  /** Map of appointment ID → files visible in that appointment */
  filesByAppointment: Map<string, FileDisplayData[]>;
  /** Metadata for all appointments in the chain */
  appointmentMap: Map<string, LinkedAppointmentInfo>;
  /** The appointment currently being viewed */
  currentAppointmentId: string;
}

export interface ResolvedFile extends FileDisplayData {
  /** Which appointment originally uploaded this file, or undefined if the originator is outside the queried chain */
  ownerAppointmentId: string | undefined;
}

/**
 * Follows rescheduledToAppointmentID forward from the given appointment
 * to find the terminal (non-rescheduled) appointment in the chain.
 * Used to attribute files to the active appointment rather than a rescheduled one.
 */
function getTerminalRescheduleTarget(
  appointmentId: string,
  appointmentMap: Map<string, LinkedAppointmentInfo>,
): string {
  let current = appointmentId;
  const visited = new Set<string>([current]);
  let next = appointmentMap.get(current)?.rescheduledToAppointmentID;

  while (next && appointmentMap.has(next) && !visited.has(next)) {
    current = next;
    visited.add(current);
    next = appointmentMap.get(current)?.rescheduledToAppointmentID;
  }

  return current;
}

/**
 * Finds all appointments in the same reschedule graph as the given appointment
 * using BFS over rescheduledFromAppointmentID / rescheduledToAppointmentID links.
 *
 * Treats reschedule links as undirected edges, so orphaned appointments
 * (e.g. cancelled holds with stale back-links) are included in the graph.
 */
function getRescheduleChain(
  appointmentId: string,
  appointmentMap: Map<string, LinkedAppointmentInfo>,
): Set<string> {
  // Build undirected adjacency list from reschedule links
  const edges = new Map<string, Set<string>>();

  const addEdge = (a: string, b: string) => {
    if (!edges.has(a)) edges.set(a, new Set());
    if (!edges.has(b)) edges.set(b, new Set());
    edges.get(a)!.add(b);
    edges.get(b)!.add(a);
  };

  for (const [id, appt] of appointmentMap) {
    if (
      appt.rescheduledToAppointmentID &&
      appointmentMap.has(appt.rescheduledToAppointmentID)
    ) {
      addEdge(id, appt.rescheduledToAppointmentID);
    }
    if (
      appt.rescheduledFromAppointmentID &&
      appointmentMap.has(appt.rescheduledFromAppointmentID)
    ) {
      addEdge(id, appt.rescheduledFromAppointmentID);
    }
  }

  // BFS from the starting appointment
  const visited = new Set<string>();
  const queue = [appointmentId];

  while (queue.length > 0) {
    const current = queue.pop()!;
    if (visited.has(current)) continue;
    if (!appointmentMap.has(current)) continue;
    visited.add(current);
    for (const neighbor of edges.get(current) ?? []) {
      queue.push(neighbor);
    }
  }

  return visited;
}

/**
 * Resolves file ownership across all linked appointments.
 *
 * Collects the deduplicated set of files across all appointments. For the current
 * appointment's reschedule chain, all files are treated as "current" (editable)
 * except non-inheritable file types (e.g. Notice 110) from other appointments.
 * The current appointment's own Notice 110 remains current.
 *
 * Files from outside the reschedule chain (reevals, supplementals, depositions)
 * are always read-only unless they were originally uploaded by the current appointment.
 *
 * The returned `referenceStatus`, `isReadOnly`, `appointmentID` and
 * `ownerAppointmentId` are all re-resolved **relative to the current
 * appointment** and are guaranteed mutually consistent — callers can trust
 * `referenceStatus` (ORIGINAL / INHERITED_WRITEABLE / INHERITED_READ_ONLY)
 * directly. The incoming `referenceStatus` is ignored (under the
 * bySourceAppointment read path every file arrives stamped ORIGINAL).
 */
export function resolveFileOwnership({
  filesByAppointment,
  appointmentMap,
  currentAppointmentId,
}: ResolveFileOwnershipParams): ResolvedFile[] {
  const rescheduleChain = getRescheduleChain(
    currentAppointmentId,
    appointmentMap,
  );

  // Deduplicate files and track which appointment originally uploaded each one
  const fileOriginator = new Map<string, string>();
  const seenFiles = new Map<string, FileDisplayData>();

  for (const [appointmentId, appointmentFiles] of filesByAppointment) {
    for (const file of appointmentFiles) {
      if (!seenFiles.has(file.id)) {
        seenFiles.set(file.id, file);
      }
      if (file.referenceStatus === AppointmentFileReferenceStatus.ORIGINAL) {
        fileOriginator.set(file.id, appointmentId);
      }
    }
  }

  // Resolve each file's "current" status
  const resolvedFiles: ResolvedFile[] = [];

  for (const [, file] of seenFiles) {
    const originatorId = fileOriginator.get(file.id);

    // Every file should have exactly one ORIGINAL reference across the queried
    // appointments. If not found, the originating appointment is outside the
    // user's access scope (e.g. Historian viewing a re-eval without access to
    // the original) — treat as read-only.
    if (!originatorId) {
      console.warn(
        `[resolveFileOwnership] File ${file.id} has no ORIGINAL reference — treating as read-only (originator outside queried chain)`,
      );
      resolvedFiles.push({
        ...file,
        ownerAppointmentId: undefined,
        isReadOnly: true,
        // Re-resolve relative to the current appointment: originator is outside
        // the queried scope, so it's an inherited read-only file here regardless
        // of the (source-stamped) referenceStatus it arrived with.
        referenceStatus: AppointmentFileReferenceStatus.INHERITED_READ_ONLY,
      });
      continue;
    }

    const isFromRescheduleChain = rescheduleChain.has(originatorId);
    const isFromCurrentAppointment = originatorId === currentAppointmentId;

    let isCurrent: boolean;

    if (isFromCurrentAppointment) {
      // Files the current appointment uploaded are always current
      isCurrent = true;
    } else if (isFromRescheduleChain) {
      // Files from the reschedule chain are current unless non-inheritable
      isCurrent = !NON_INHERITABLE_FILE_TYPES.includes(file.fileType);
    } else {
      // Files from outside the reschedule chain are read-only
      isCurrent = false;
    }

    // Current files are owned by this appointment (editable);
    // non-current files are attributed to the terminal appointment in the
    // originator's reschedule chain so rescheduled appointments stay hidden.
    const ownerId = isCurrent
      ? currentAppointmentId
      : getTerminalRescheduleTarget(originatorId, appointmentMap);

    // Re-resolve referenceStatus relative to the current appointment so it can
    // never conflict with isReadOnly. The incoming value is the source stamp
    // (e.g. the bySourceAppointment read marks every file ORIGINAL), which is
    // not meaningful from this appointment's perspective.
    const resolvedReferenceStatus = isFromCurrentAppointment
      ? AppointmentFileReferenceStatus.ORIGINAL
      : isCurrent
        ? AppointmentFileReferenceStatus.INHERITED_WRITEABLE
        : AppointmentFileReferenceStatus.INHERITED_READ_ONLY;

    resolvedFiles.push({
      ...file,
      ownerAppointmentId: ownerId,
      isReadOnly: !isCurrent,
      referenceStatus: resolvedReferenceStatus,
      appointmentID: ownerId,
    });
  }

  return resolvedFiles;
}
